/**
 * live.ts — shared constants/helpers for the co-driven ("live") demo.
 *
 * LIVE is a build-time constant: true only in `npm run dev:live` / `build:live`
 * (VITE_DEMO_LIVE=1). In the static demo it is false and every live component
 * short-circuits, so none of the relay/video code ever runs.
 */

/** True only in a live build. Gate all relay/video behavior on this. */
export const LIVE = import.meta.env.VITE_DEMO_LIVE === "1";

/** ws(s):// relay base (e.g. wss://relay.psy180.com). Empty in static builds. */
export const RELAY_WS = (import.meta.env.VITE_RELAY_URL as string | undefined) ?? "";

/** http(s):// sibling of the relay, for the credentials GET. */
export const RELAY_HTTP = RELAY_WS
  ? RELAY_WS.replace(/^wss:/, "https:").replace(/^ws:/, "http:")
  : "";

export type LiveRole = "host" | "guest";

/**
 * HOST AUTH — a short-lived token (not a durable key in the URL). The host logs in
 * with a PASSWORD at /live; the relay returns a 12h HMAC-signed token which authorizes
 * host_join + TURN-cred minting. The token lives in sessionStorage (per-tab), never in
 * the URL/bookmark/history, and expires on its own. The relay is the real enforcement.
 */
const HOST_TOKEN_STORAGE = "psy180_live_host_token";

/** The current unexpired host token, or null (also clears an expired one). */
export function getHostToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(HOST_TOKEN_STORAGE);
    if (!raw) return null;
    const { token, expiresAt } = JSON.parse(raw) as { token: string; expiresAt: number };
    // 30s skew guard so we never send a token about to expire mid-request.
    if (!token || typeof expiresAt !== "number" || expiresAt * 1000 <= Date.now() + 30_000) {
      window.sessionStorage.removeItem(HOST_TOKEN_STORAGE);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function clearHostToken(): void {
  try {
    window.sessionStorage.removeItem(HOST_TOKEN_STORAGE);
  } catch {
    /* ignore */
  }
}

/** Exchange the host password for a token. Returns true on success. */
export async function hostLogin(password: string): Promise<boolean> {
  if (!RELAY_HTTP) return false;
  try {
    const res = await fetch(`${RELAY_HTTP}/host-auth`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) return false;
    const { token, expiresAt } = await res.json();
    if (typeof token !== "string" || typeof expiresAt !== "number") return false;
    window.sessionStorage.setItem(HOST_TOKEN_STORAGE, JSON.stringify({ token, expiresAt }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Optional STANDING room from the host bookmark (`/live#k=…&room=<code>`).
 * With it, the host always opens the SAME room — whose guest link
 * (demo.psy180.com/live/<code>) can live in Cal.com booking emails: prospects
 * open it whenever, sit in a waiting state, and connect the moment the host
 * enters "on command". Without it, LiveEntry mints a fresh ad-hoc code (the
 * copy-the-link-in-chat flow). Same 8–64 url-safe charset the relay enforces.
 */
export function getHostRoom(): string | null {
  if (typeof window === "undefined") return null;
  const m = window.location.hash.match(/[#&]room=([A-Za-z0-9_-]{8,64})/);
  return m ? m[1] : null;
}

/**
 * Generate a URL-safe room code that satisfies the relay's DEMO_CODE_REGEX
 * (8–64 of [A-Za-z0-9_-]). ~16 chars of crypto randomness → not guessable/
 * scannable, still short enough to sit in a shareable link.
 */
export function generateRoomCode(): string {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789"; // no look-alikes
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let code = "";
  for (const b of bytes) code += alphabet[b % alphabet.length];
  return code;
}

/** The shareable guest link for a room, pinned to the demo origin. */
export function guestLink(code: string): string {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://demo.psy180.com";
  return `${origin}/live/${code}`;
}
